import { NodeEditor } from 'modules/workflows/components/NodeEditor';
import { getIsNodeEditorReadOnly, updateNodeData } from 'modules/workflows/ducks';
import { WorkflowType } from 'modules/workflows/enums';
import { useDisplayCloneErrors } from 'modules/workflows/hooks/useDisplayCloneErrors';
import { SendToWorkflowNodeData, Workflow } from 'modules/workflows/types';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BUTTON_SIZES } from 'ui/button';
import { colors } from 'ui/theme';
import { Link } from 'ui/links';
import { HelpToggletip, TipPlacement } from 'ui/tooltip';
import { FormSectionContent, RowWrapper } from '../../EditorBody.styled';
import { EditorBaseProps } from '../../types';
import { CreateWorkflowModal } from './CreateWorkflowModal';
import { useCreateNewWorkflowDraft } from './hooks/useCreateNewWorkflow';
import { SelectedWorkflowData } from './SelectedWorkflowData';
import {
  SelectedWorkflowDetails,
  StyledButton,
  StyledFormField,
  StyledWorkflowSelect,
} from './TriggerWorkflowEditor.styles';
import { getEditWorkflowDraftLink, getEditWorkflowLink } from './utils';

export const TRIGGER_WORKFLOW_TEST_IDS = {
  ID: 'workflow-select',
  EDITOR: 'trigger-workflow-editor',
  VIEW_LINK: 'view-workflow-link',
  CREATE_NEW: 'create-new-workflow-button',
};

export type TriggerWorkflowProps = EditorBaseProps<SendToWorkflowNodeData>;

export const TriggerWorkflowEditor = (props: TriggerWorkflowProps) => {
  const { nodeData, nodeId, clonedFromNodeData, ...rest } = props;
  const dispatch = useDispatch();
  const [selectedWorkflow, setSelectedWorkflow] = useState<Partial<Workflow>>({
    id: nodeData.workflowId,
    name: nodeData.resource?.name,
    workflowType: nodeData.workflowType || WorkflowType.PUBLISHED, // remove this later
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const isReadOnly = useSelector(getIsNodeEditorReadOnly);
  const {
    isCloneWarningBannerShown = false,
    cloneErrors = {},
    cloneDisplayValues = {},
  } = useDisplayCloneErrors({
    clonedFromNodeData,
    fieldsToValidate: {
      workflowId: {
        isValid: selectedWorkflow.id,
        displayValue: clonedFromNodeData?.resource?.name,
      },
    },
  });

  const handleChange = (item: any) => {
    setSelectedWorkflow((wf) => ({
      id: Number(item?.id) || wf.id,
      name: item?.name || wf.name,
      workflowType: item?.workflowType || wf.workflowType,
    }));
    setHasError(false);
  };

  const createNewWorkflow = useCreateNewWorkflowDraft();

  const handleCreateWorkflow = (name: string) =>
    createNewWorkflow({
      name,
      onSuccess: (workflow: any) => {
        setIsModalOpen(false);
        setSelectedWorkflow(workflow as unknown as Workflow);
      },
    });

  const handleUpdate = (requiresValidation = true) => {
    const { id, name, workflowType } = selectedWorkflow;
    if (!id && requiresValidation) return setHasError(true);
    const updatedNodeData = {
      workflowId: id,
      workflowType: workflowType || WorkflowType.PUBLISHED, // remove after we switch completely to drafts
      resource: { name },
    } as any;
    dispatch(updateNodeData(updatedNodeData, nodeId));
  };

  return (
    <NodeEditor
      {...rest}
      dataTest={TRIGGER_WORKFLOW_TEST_IDS.EDITOR}
      onUpdate={handleUpdate}
      /** These are used to check if node has changes */
      initialNodeData={{ workflowId: nodeData.workflowId }}
      updatedNodeData={{ workflowId: selectedWorkflow?.id }}
      isCloneWarningBannerShown={isCloneWarningBannerShown}
    >
      <FormSectionContent>
        <StyledFormField
          errorText="Please link a journey."
          hasError={hasError || cloneErrors.workflowId}
          labelText={
            <>
              Link journey&nbsp;
              <HelpToggletip
                placement={TipPlacement.Right}
                tooltipContent="Don’t see the journey you’re looking for? Make sure the journey’s trigger is set to “Other journey(s)”."
                iconProps={{ color: colors.secondaryText }}
              />
            </>
          }
          inputId={TRIGGER_WORKFLOW_TEST_IDS.ID}
        >
          <RowWrapper>
            <StyledWorkflowSelect
              workflow={props.workflowData}
              onChange={handleChange}
              displayValue={cloneErrors?.workflowId ? cloneDisplayValues?.workflowId : ''}
              value={selectedWorkflow?.id ? `${selectedWorkflow.id}` : ''}
              hasError={hasError || cloneErrors.workflowId}
              isReadOnly={isReadOnly}
            />
            <StyledButton
              data-test={TRIGGER_WORKFLOW_TEST_IDS.CREATE_NEW}
              size={BUTTON_SIZES.LARGE}
              onClick={() => setIsModalOpen(true)}
              isDisabled={isReadOnly}
            >
              Create new
            </StyledButton>
          </RowWrapper>
        </StyledFormField>
        {selectedWorkflow?.id && (
          <SelectedWorkflowDetails>
            <SelectedWorkflowData selectedWorkflow={selectedWorkflow} />
            <Link
              hasOpenNewTabIcon
              target="_blank"
              href={
                selectedWorkflow.workflowType === WorkflowType.DRAFT
                  ? getEditWorkflowDraftLink(selectedWorkflow.id)
                  : getEditWorkflowLink(selectedWorkflow.id)
              }
              rel="noreferrer noopener"
              data-test={TRIGGER_WORKFLOW_TEST_IDS.VIEW_LINK}
            >
              Open journey
            </Link>
          </SelectedWorkflowDetails>
        )}
      </FormSectionContent>
      {isModalOpen && (
        <CreateWorkflowModal
          onClose={() => setIsModalOpen(false)}
          onCreate={handleCreateWorkflow}
        />
      )}
    </NodeEditor>
  );
};
